/**
 * The source rules for relocating stock out of a railcar into the yard.
 *
 * The crew empties a car by hand — most often lumber left behind after the
 * inbound was worked — and until now the app had no way to record it. The move
 * itself is an ordinary relocate; what is special is the source. A track is an
 * unmanaged location under an Area of type TRACK, and inventory that sits there
 * is inventory that is still physically in the car.
 *
 * Two callers ask these questions at different moments, and they read them from
 * here so they cannot drift apart: the relocate card disables its Save button
 * while a rule refuses, and `handleSave` asks again against a freshly read item,
 * because the page may have been open since before a manager released the car.
 *
 * What these rules deliberately do NOT refuse is a released or collected car.
 * Relocating out of a departed car is the whole point of the feature: the pick
 * side blocks committing inventory that is still on such a car (see
 * `releasedRailcar.js`), and moving the stock into a yard location is the
 * remedy — it is also what physically happened.
 */
import { InventoryStatus } from "./constants";
import { isTrack } from "./locationUtils";

const TRACK_DESTINATION_REFUSAL =
  "Inventory cannot be relocated onto a railcar. " +
  "Choose a yard or warehouse location.";

const CLOSED_HEADER_REFUSAL =
  "This inventory was closed out when the railcar was released and can no " +
  "longer be relocated.";

/**
 * Where the unit physically is. The item's own areaId is canonical; the
 * location's area answers for an item whose areaId was never written, and the
 * parent inventory's area is the last resort for aggregate rows that predate
 * items carrying an area of their own. This is the same resolution order
 * `releasedRailcar.js` uses, on purpose — the two modules have to agree about
 * which stock is "in the car" or the yard gets a move the pick side then
 * refuses to honour.
 */
const sourceArea = (inventoryItem) => {
  if (inventoryItem?.areaId) return inventoryItem.areaId;
  if (inventoryItem?.location?.area) return inventoryItem.location.area;
  return inventoryItem?.inventory?.areaId;
};

/**
 * `isTrack` reads `areaType` off an Area object and resolves a plain id through
 * the yard's areaMap, so an object that carries an id but no areaType — a value
 * straight out of a select, say — would answer "not a track" for want of a
 * field rather than on the facts. Hand such an object over as its id instead,
 * so the destination assert below cannot be defeated by a partial record.
 */
const areaRef = (area) =>
  area && typeof area === "object" && !area.areaType && area.id
    ? area.id
    : area;

/**
 * True when the item is stock still in a railcar, which is what makes this a
 * track relocate rather than an ordinary yard move. Everything below keys off
 * it so that the extra rules apply to cars only and the yard keeps the
 * behaviour it has today.
 *
 * @param {object} inventoryItem - Inventory item being relocated.
 * @returns {boolean} True when the item's area is a TRACK area.
 */
export const isTrackRelocateSource = (inventoryItem) =>
  Boolean(inventoryItem) && isTrack(areaRef(sourceArea(inventoryItem)));

/**
 * Refuses a relocate the yard must not record, in the crew's words.
 *
 * Two rules, checked in the order the crew would notice them:
 *
 * 1. The destination is never a railcar. Car → car is out of scope: a track
 *    holds several cars behind one Location, so nothing on the item would say
 *    which car it landed in, and the merge path would quietly combine the stock
 *    of two cars. A car is loaded through inbound placement or the outbound
 *    pick list, never through relocate. The destination list already leaves
 *    TRACK out; this is the assert that keeps a later change to that filter
 *    from opening car → car through the back door.
 * 2. The inventory header must still be open, for a track source only.
 *    Releasing a rail inbound closes its inventory out — bulk stored on the
 *    track goes SHIPPED and a SIT car goes RELEASED (see
 *    `InboundUtils.transformLamdaInventories`) — and relocating stock under a
 *    closed header resurrects quantity the books have already written off. The
 *    non-bulk rail stock this feature exists for stays STORED through the
 *    release, so the lumber case passes. Scoped to track sources deliberately:
 *    an ordinary yard relocate is left exactly as it behaves today.
 *
 * @param {object} args
 * @param {object} args.inventoryItem - Item the crew is moving.
 * @param {object|string} [args.destinationArea] - Chosen destination Area, or
 *   its id; undefined while the crew has not picked one yet.
 * @returns {string|null} Message to show the crew, or null when the move may go
 *   ahead.
 */
export const validateTrackRelocate = ({ inventoryItem, destinationArea }) => {
  if (destinationArea && isTrack(areaRef(destinationArea)))
    return TRACK_DESTINATION_REFUSAL;

  if (!inventoryItem) return null;
  if (!isTrackRelocateSource(inventoryItem)) return null;

  if (inventoryItem.inventory?.status !== InventoryStatus.STORED)
    return CLOSED_HEADER_REFUSAL;

  return null;
};

/**
 * The fields that record who emptied the car, or null when there is nothing to
 * record.
 *
 * Taking the last of a commodity out of a car by hand is the unload, whether it
 * happens during the inbound or weeks later, so the move is the moment to stamp
 * it. Most items arrive already stamped by the crew placement flow
 * (`InboundAisleMap`, `ShipmentItem`), which is why this only fills the gap
 * left by manager-created and stock-imported rows.
 *
 * An existing stamp is never overwritten, not even a half one: the crew that
 * first unloaded the car owns that record, and a later yard shuffle has no
 * business rewriting it.
 *
 * `unloadedAt` is an ISO string. The placement flows hand the same field a Date
 * object, which serialises to exactly that on the way to the AWSDateTime field;
 * writing the string here says what reaches the API and keeps the value stable
 * for anything that compares stamps.
 *
 * @param {object} inventoryItem - Item being relocated out of the car.
 * @returns {{unloadedBy: string, unloadedAt: string}|null} Fields to merge into
 *   the item being written, or null to leave the stamps alone.
 */
export const getUnloadStamp = (inventoryItem) => {
  if (!isTrackRelocateSource(inventoryItem)) return null;
  if (inventoryItem.unloadedBy || inventoryItem.unloadedAt) return null;
  return {
    unloadedBy: window.appUser?.fullName,
    unloadedAt: new Date().toISOString(),
  };
};
